'use client'

import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs'

import { CategoryFilterChip } from '@/components/marketing/category-filter-chip'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const SEARCH_DEBOUNCE_MS = 300

export type BlogFilterBarProps = {
  categories: { slug: string; name: string }[]
}

/**
 * Sticky search + category chip row for `/blog`. All filter state lives in
 * the URL via nuqs (search + category are client-driven; page numbers stay
 * real crawlable `<Link>`s — see `BlogPagination`). Hand-rolled marketing
 * chips per plan.md decision #2 — the dashboard `FilterToolbar` register
 * doesn't fit a marketing page (see the `/glossary` category-filter
 * precedent this mirrors).
 */
export function BlogFilterBar({ categories }: BlogFilterBarProps): React.ReactElement {
  const [q, setQ] = useQueryState('q', parseAsString.withDefault(''))
  const [category, setCategory] = useQueryState('category', parseAsString.withDefault('all'))
  const [, setPage] = useQueryState('page', parseAsInteger.withDefault(1))

  // Local echo of `q` so typing doesn't push a URL update on every keystroke.
  const [searchValue, setSearchValue] = useState(q)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keep the input in sync with back/forward navigation or external URL changes.
  useEffect(() => {
    setSearchValue(q)
  }, [q])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  function handleSearchChange(value: string) {
    setSearchValue(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      void setQ(value.trim() ? value : null)
      void setPage(null)
    }, SEARCH_DEBOUNCE_MS)
  }

  function handleClear() {
    setSearchValue('')
    if (debounceRef.current) clearTimeout(debounceRef.current)
    void setQ(null)
    void setPage(null)
  }

  function handleCategoryChange(slug: string) {
    void setCategory(slug === 'all' ? null : slug)
    void setPage(null)
  }

  return (
    <div className="sticky top-16 z-30 border-b border-border bg-card/95 backdrop-blur">
      <div className="wrap-wide flex flex-col gap-4 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search
            size={16}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="text"
            autoComplete="off"
            value={searchValue}
            onChange={(event) => handleSearchChange(event.target.value)}
            placeholder="Search articles"
            aria-label="Search articles"
            className={cn('pl-9', searchValue && 'pr-9')}
          />
          {searchValue && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear search"
              className="absolute top-1/2 right-2.5 -translate-y-1/2 text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              <X size={15} aria-hidden="true" />
            </button>
          )}
        </div>

        <div
          className="flex flex-wrap items-center gap-2"
          role="group"
          aria-label="Filter by category"
        >
          {[{ slug: 'all', name: 'All' }, ...categories].map((c) => (
            <CategoryFilterChip
              key={c.slug}
              label={c.name}
              active={category === c.slug}
              onClick={() => handleCategoryChange(c.slug)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
